import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ColumnDef } from "@tanstack/react-table";
import { DateOnly } from "components/common/Date";
import {
  AmcVisit,
  CompleteAmcVisitRequest,
} from "features/AmcVisitLogs/types/amc-visit.types";
import {
  cancelAmcVisit,
  completeAmcVisit,
  getAmcVisits,
  startAmcVisit,
} from "features/AmcVisitLogs/services/AmcVisitService";
import { CodeCell, IndexColumn } from "components/common/columns";
import StatusBadge from "components/common/StatusBadge";
import {
  AmcVisitStatus,
  amcVisitStatusMap,
} from "features/AmcVisitLogs/constants/amc-visit.constants";
import {
  Collapse,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import CancelModal from "../CancelModal";
import CompleteVisitModal from "../CompleteVisitModal";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import WorkAssignmentForm from "features/WorkAssignments/WorkAssignmentForm";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import {
  canReassignAmcVisit,
  shouldShowAmcVisitAssignmentActions,
} from "features/AmcVisitLogs/utils/amcVisitActionPolicy";
import VisitLogListFilters, {
  countVisitLogFiltersActive,
  emptyVisitLogFilters,
  visitLogFiltersToListParams,
  type VisitLogFiltersState,
} from "./VisitLogListFilters";
import AmcVisitAssigneesAvatarGroup from "./AmcVisitAssigneesAvatarGroup";

type VisitLogTableProps = {
  /** When set, passes amcId to GET /amc-visits to limit rows to that contract. */
  amcId?: number;
};

export const VisitLogTable = ({ amcId: urlAmcId }: VisitLogTableProps) => {
  const { can } = useAccess();
  const canReadVisit = can(Permissions.AMC_VISIT.READ);
  const canUpdateVisit = can(Permissions.AMC_VISIT.UPDATE);
  const canCompleteVisit = can(Permissions.AMC_VISIT.COMPLETE);
  const canCancelVisit = can(Permissions.AMC_VISIT.CANCEL);
  const canAssign = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canReassign = can(Permissions.WORK_ASSIGNMENT.UPDATE);

  const tableRef = useRef<() => void>(null);
  const [amcVisit, setAmcVisit] = useState<AmcVisit>();

  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [completeModal, setCompleteModal] = useState<boolean>(false);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignFormVisit, setAssignFormVisit] = useState<AmcVisit | null>(null);
  const [assignFormIsReassign, setAssignFormIsReassign] = useState(false);
  const [startBusyId, setStartBusyId] = useState<number | null>(null);
  const [listFilters, setListFilters] = useState<VisitLogFiltersState>(() =>
    emptyVisitLogFilters(),
  );
  const [filterOpen, setFilterOpen] = useState(false);

  const refreshTable = useCallback(() => {
    tableRef.current?.();
  }, []);

  const listRequestParams = useMemo(() => {
    const fromFilters = visitLogFiltersToListParams(listFilters);
    const filterAmcId =
      fromFilters.amcId != null &&
      Number.isFinite(Number(fromFilters.amcId)) &&
      Number(fromFilters.amcId) > 0
        ? Number(fromFilters.amcId)
        : undefined;
    const fromUrl =
      urlAmcId != null && urlAmcId > 0 ? Number(urlAmcId) : undefined;
    const effectiveAmcId = filterAmcId ?? fromUrl;
    const { amcId: _fromFiltersAmc, ...restFilters } = fromFilters;
    return {
      ...restFilters,
      ...(effectiveAmcId != null ? { amcId: effectiveAmcId } : {}),
    };
  }, [urlAmcId, listFilters]);

  const filtersBadgeCount = useMemo(
    () => countVisitLogFiltersActive(listFilters),
    [listFilters],
  );

  const clearListFilters = useCallback(() => {
    setListFilters(emptyVisitLogFilters());
  }, []);

  const onClickCancel = useCallback((record: AmcVisit) => {
    setAmcVisit(record);
    setCancelModal(true);
  }, []);

  const onClickComplete = useCallback((record: AmcVisit) => {
    setAmcVisit(record);
    setCompleteModal(true);
  }, []);

  const onClickStartVisit = useCallback(async (record: AmcVisit) => {
    setStartBusyId(record.id);
    try {
      const response = await startAmcVisit(record.id);
      if (response.success) {
        toast.success(response.message);
        tableRef.current?.();
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not start visit",
      );
    } finally {
      setStartBusyId(null);
    }
  }, []);

  const closeAssignmentForm = useCallback(() => {
    setAssignFormOpen(false);
    setAssignFormVisit(null);
    setAssignFormIsReassign(false);
  }, []);

  const openAssign = useCallback((record: AmcVisit) => {
    setAssignFormIsReassign(false);
    setAssignFormVisit(record);
    setAssignFormOpen(true);
  }, []);

  const openReassign = useCallback((record: AmcVisit) => {
    if (!canReassignAmcVisit(record.status)) {
      toast.info(
        "Completed, cancelled, or skipped visits cannot be reassigned.",
      );
      return;
    }
    setAssignFormIsReassign(true);
    setAssignFormVisit(record);
    setAssignFormOpen(true);
  }, []);

  const handleCancel = async (reason?: string) => {
    if (amcVisit) {
      try {
        const response = await cancelAmcVisit(amcVisit.id, reason);
        if (response.success) {
          toast.success(response.message);
          setCancelModal(false);
          tableRef.current?.();
        }
      } catch (err: unknown) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not cancel visit",
        );
      }
    }
  };

  const handleComplete = async (body: CompleteAmcVisitRequest) => {
    if (!amcVisit) return;
    try {
      const response = await completeAmcVisit(amcVisit.id, body);
      if (response.success) {
        toast.success(response.message);
        tableRef.current?.();
      }
      setCompleteModal(false);
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not complete visit",
      );
    }
  };

  const columns: ColumnDef<AmcVisit>[] = useMemo(
    () => [
      IndexColumn<AmcVisit>(),
      {
        header: "Code",
        cell: ({ row }) => {
          const r = row.original;
          return canReadVisit ? (
            <Link to={`/amc-management/visit-logs/${r.id}`} className="text-decoration-none link">
              <CodeCell code={r.code} />
            </Link>
          ) : (
            <CodeCell code={r.code} />
          );
        },
        accessorKey: "code",
        enableColumnFilter: false,
      },

      {
        header: "Contract",
        accessorKey: "amcId",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const c = row.original.contract;
          const code = c?.code || `Contract #${row.original.amcId}`;
          const title = c?.title?.trim() || null;
          return (
            <Link to={`/amc-management/${row.original.amcId}`} className="link">
              <span className="fw-medium">{code}</span>
              {title ? (
                <div className="small text-muted fw-normal">{title}</div>
              ) : null}
            </Link>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const val = row.original.status;
          return <StatusBadge value={val} map={amcVisitStatusMap} />;
        },
      },
      {
        header: "Assigned",
        id: "assigned",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const canAssignMore =
            shouldShowAmcVisitAssignmentActions(r.status) && canAssign;
          return (
            <AmcVisitAssigneesAvatarGroup
              visit={r}
              assignments={r.workAssignments}
              visitId={r.id}
              canReadDetail={canReadVisit}
              canAssignMore={canAssignMore}
              onOpenAssign={openAssign}
            />
          );
        },
      },
      {
        header: "Scheduled On",
        accessorKey: "scheduledAt",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.scheduledAt ? (
            <DateOnly date={row.original.scheduledAt} />
          ) : (
            "—"
          ),
      },

      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const base = `/amc-management/visit-logs`;
          const isEditable = r.status === AmcVisitStatus.PLANNED;
          const isCancellable = r.status === AmcVisitStatus.PLANNED;
          const isActionable = shouldShowAmcVisitAssignmentActions(r.status);
          const showAssignmentItems = isActionable && (canAssign || canReassign);
          const showEdit = canUpdateVisit && isEditable;
          const showCancel = canCancelVisit && isCancellable;
          const hasMenuItems =
            canReadVisit ||
            showEdit ||
            canCompleteVisit ||
            showCancel ||
            showAssignmentItems;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }

          const hasTopItems = canReadVisit || showEdit;
          const hasItemsBeforeStart = hasTopItems || showAssignmentItems;
          const hasItemsBeforeComplete =
            hasTopItems || showAssignmentItems || canUpdateVisit;
          const hasItemsBeforeCancel =
            hasTopItems ||
            showAssignmentItems ||
            canUpdateVisit ||
            canCompleteVisit;

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Visit log actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canReadVisit ? (
                  <DropdownItem tag={Link} to={`${base}/${r.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {showEdit ? (
                  <DropdownItem tag={Link} to={`${base}/edit/${r.id}`}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {showAssignmentItems ? (
                  <>
                    {hasTopItems ? <DropdownItem divider /> : null}
                    {canAssign ? (
                      <DropdownItem onClick={() => openAssign(r)}>
                        <i className="ri-user-follow-line align-bottom me-2 text-muted" />
                        Assign user
                      </DropdownItem>
                    ) : null}
                    {canReassign ? (
                      <DropdownItem onClick={() => openReassign(r)}>
                        <i className="ri-user-shared-line align-bottom me-2 text-muted" />
                        Reassign
                      </DropdownItem>
                    ) : null}
                  </>
                ) : null}
                {canUpdateVisit && r.status === AmcVisitStatus.PLANNED ? (
                  <>
                    {hasItemsBeforeStart ? <DropdownItem divider /> : null}
                    <DropdownItem
                      disabled={startBusyId === r.id}
                      onClick={() => void onClickStartVisit(r)}
                    >
                      <i className="ri-play-line align-bottom me-2 text-muted" />
                      {startBusyId === r.id ? "Starting…" : "Start visit"}
                    </DropdownItem>
                  </>
                ) : null}
                {canCompleteVisit && r.status === AmcVisitStatus.IN_PROGRESS ? (
                  <>
                    {hasItemsBeforeComplete ? <DropdownItem divider /> : null}
                    <DropdownItem onClick={() => onClickComplete(r)}>
                      <i className="ri-check-double-line align-bottom me-2 text-muted" />
                      Complete visit
                    </DropdownItem>
                  </>
                ) : null}
                {showCancel ? (
                  <>
                    {hasItemsBeforeCancel ? <DropdownItem divider /> : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickCancel(r)}
                    >
                      <i className="ri-close-circle-line align-bottom me-2" />
                      Cancel visit
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      canReadVisit,
      canUpdateVisit,
      canCompleteVisit,
      canCancelVisit,
      canAssign,
      canReassign,
      onClickCancel,
      onClickComplete,
      onClickStartVisit,
      openAssign,
      openReassign,
      startBusyId,
    ],
  );

  return (
    <React.Fragment>
      <CancelModal
        show={cancelModal}
        onCancelClick={handleCancel}
        onCloseClick={() => setCancelModal(false)}
        record={amcVisit}
      />

      <CompleteVisitModal
        show={completeModal}
        onCompleteClick={handleComplete}
        onCloseClick={() => setCompleteModal(false)}
        record={amcVisit}
      />

      <WorkAssignmentForm
        show={assignFormOpen && assignFormVisit != null}
        sourceType={WorkAssignmentSourceType.AMC_VISIT}
        sourceId={assignFormVisit?.id ?? 0}
        sourceLabel={
          assignFormVisit
            ? assignFormVisit.code
              ? `AMC visit ${assignFormVisit.code}`
              : `AMC visit #${assignFormVisit.id}`
            : undefined
        }
        isReassign={assignFormIsReassign}
        defaultPlannedStartAt={assignFormVisit?.scheduledAt}
        onCloseClick={closeAssignmentForm}
        onSuccess={refreshTable}
      />

      <TableContainer
        columns={columns}
        fetchApi={getAmcVisits}
        pageSize={10}
        globalSearch={true}
        refreshRef={tableRef}
        requestParams={listRequestParams}
        SearchPlaceholder="Search visits…"
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
        toolbarRight={
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className={`btn d-flex align-items-center gap-1 ${filterOpen ? "btn-primary" : "btn-soft-secondary"}`}
              onClick={() => setFilterOpen((v) => !v)}
              title="Toggle filters"
            >
              <i className="ri-filter-3-line" />
              Filters
              {filtersBadgeCount > 0 && (
                <span className={`badge rounded-pill ${filterOpen ? "bg-white text-primary" : "bg-primary text-white"}`}>
                  {filtersBadgeCount}
                </span>
              )}
            </button>
            {filtersBadgeCount > 0 && (
              <button
                type="button"
                className="btn btn-soft-danger d-flex align-items-center gap-1"
                onClick={clearListFilters}
              >
                <i className="ri-close-circle-line" />
                Clear
              </button>
            )}
          </div>
        }
        filterPanel={
          <Collapse isOpen={filterOpen}>
            <div className="px-1 pt-1 pb-3 border-bottom mb-3">
              <VisitLogListFilters filters={listFilters} onChange={setListFilters} />
            </div>
          </Collapse>
        }
      />
    </React.Fragment>
  );
};
